import React, { useState, useEffect } from "react";
import axios from "axios";
import AdminMenu from "../../components/Layout/AdminMenu";
import Layout from "../../components/Layout/Layout";
import { useAuth } from "../../context/auth";
import moment from "moment";
import "../../styles/AdminStyles.css";

const AdminPayments = () => {
  const [auth] = useAuth();
  const [payments, setPayments] = useState([]);
  const [stats, setStats] = useState(null);
  const [filter, setFilter] = useState("all");
  const [methodFilter, setMethodFilter] = useState("all");
  const [expandedId, setExpandedId] = useState(null);
  const [search, setSearch] = useState("");
  const [actionLoading, setActionLoading] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);

  const fetchPayments = async () => {
    try {
      const { data } = await axios.get("/api/v1/payments/all");
      if (data?.success) setPayments(data.payments);
    } catch (error) {
      console.log(error);
    }
  };

  const fetchStats = async () => {
    try {
      const { data } = await axios.get("/api/v1/payments/stats");
      if (data?.success) setStats(data.stats);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    if (auth?.token) {
      fetchPayments();
      fetchStats();
    }
  }, [auth?.token]);

  const handleRefund = async (payment) => {
    setActionLoading(payment._id);
    try {
      const { data } = await axios.post(`/api/v1/payments/${payment._id}/refund`, {
        amount: payment.totalAmount,
        reason: "استرجاع من لوحة التحكم",
      });
      if (data?.success) {
        const p = data.payment;
        alert(
          `${data.message}\n\n` +
          `الفاتورة: ${p.invoiceNumber}\n` +
          `Qi Payment ID: ${p.qiPaymentId}\n` +
          `Transaction ID: ${p.transactionId || "-"}\n` +
          `المبلغ المسترجع: ${(p.refundAmount || 0).toLocaleString("ar-IQ")} د.ع`
        );
        fetchPayments();
        fetchStats();
      } else {
        alert(data.message || "فشل الاسترجاع");
      }
    } catch (error) {
      alert(error.response?.data?.message || "حدث خطأ في الاسترجاع");
    }
    setActionLoading(null);
    setConfirmAction(null);
  };

  const handleCancel = async (payment) => {
    setActionLoading(payment._id);
    try {
      const { data } = await axios.post(`/api/v1/payments/${payment._id}/cancel`);
      if (data?.success) {
        const p = data.payment;
        alert(
          `${data.message}\n\n` +
          `الفاتورة: ${p.invoiceNumber}\n` +
          `Qi Payment ID: ${p.qiPaymentId || "-"}`
        );
        fetchPayments();
        fetchStats();
      } else {
        alert(data.message || "فشل الإلغاء");
      }
    } catch (error) {
      alert(error.response?.data?.message || "حدث خطأ في الإلغاء");
    }
    setActionLoading(null);
    setConfirmAction(null);
  };

  const formatPrice = (price) => (price || 0).toLocaleString("ar-IQ") + " د.ع";

  const getStatusLabel = (status) => {
    const map = {
      pending: "بانتظار الدفع",
      processing: "قيد المعالجة",
      completed: "مكتمل",
      failed: "فشل",
      refunded: "مسترجع",
      cancelled: "ملغي",
      expired: "منتهي",
    };
    return map[status] || status;
  };

  const getStatusClass = (status) => {
    const map = {
      pending: "ap-badge-pending",
      processing: "ap-badge-processing",
      completed: "ap-badge-completed",
      failed: "ap-badge-failed",
      refunded: "ap-badge-refunded",
      cancelled: "ap-badge-failed",
      expired: "ap-badge-failed",
    };
    return map[status] || "";
  };

  const getMethodLabel = (method) => {
    const map = {
      qi: "Qi Card",
      cash_on_delivery: "عند الاستلام",
      braintree: "بطاقة ائتمان",
    };
    return map[method] || method;
  };

  const getQiStatusLabel = (status) => {
    if (!status) return "-";
    const map = {
      CREATED: "تم الإنشاء",
      SUCCESS: "ناجح",
      FAILED: "فاشل",
      AUTHENTICATION_FAILED: "فشل المصادقة",
      AMOUNT_MISMATCH: "عدم تطابق المبلغ",
    };
    return map[status] || status;
  };

  // Filtered payments
  const filtered = payments.filter((p) => {
    if (filter !== "all" && p.paymentStatus !== filter) return false;
    if (methodFilter !== "all" && p.paymentMethod !== methodFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      const match =
        p.invoiceNumber?.toLowerCase().includes(q) ||
        p.customer?.name?.toLowerCase().includes(q) ||
        p.customer?.phone?.includes(q) ||
        p.qi?.merchantReference?.toLowerCase().includes(q) ||
        p.qi?.paymentId?.toLowerCase().includes(q);
      if (!match) return false;
    }
    return true;
  });

  return (
    <Layout title={"معاملات الدفع - النخبة ستور"}>
      <div className="container-fluid admin-layout">
        <div className="row">
          <div className="col-md-3">
            <AdminMenu />
          </div>
          <div className="col-md-9">
            <div className="admin-page-header">
              <h1>معاملات الدفع</h1>
              <p>مراقبة جميع عمليات الدفع و Qi Card ({payments?.length || 0} معاملة)</p>
              <div className="admin-page-header-line"></div>
            </div>

            {/* Stats Cards */}
            {stats && (
              <div className="admin-stats-grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))" }}>
                <div className="admin-stat-card">
                  <div className="admin-stat-icon" style={{ fontSize: "1.5rem" }}>&#x1F4B0;</div>
                  <span className="admin-stat-value">{formatPrice(stats.totalRevenue)}</span>
                  <span className="admin-stat-label">إجمالي الإيرادات</span>
                </div>
                <div className="admin-stat-card">
                  <div className="admin-stat-icon" style={{ fontSize: "1.5rem" }}>&#x2705;</div>
                  <span className="admin-stat-value">{stats.completedPayments}</span>
                  <span className="admin-stat-label">مكتمل</span>
                </div>
                <div className="admin-stat-card">
                  <div className="admin-stat-icon" style={{ fontSize: "1.5rem" }}>&#x23F3;</div>
                  <span className="admin-stat-value">{stats.processingPayments + stats.pendingPayments}</span>
                  <span className="admin-stat-label">قيد الانتظار</span>
                </div>
                <div className="admin-stat-card">
                  <div className="admin-stat-icon" style={{ fontSize: "1.5rem" }}>&#x274C;</div>
                  <span className="admin-stat-value">{stats.failedPayments}</span>
                  <span className="admin-stat-label">فاشل</span>
                </div>
                <div className="admin-stat-card">
                  <div className="admin-stat-icon" style={{ fontSize: "1.5rem" }}>&#x1F4B3;</div>
                  <span className="admin-stat-value">{stats.qiCompleted}</span>
                  <span className="admin-stat-label">Qi Card مكتمل</span>
                </div>
                <div className="admin-stat-card">
                  <div className="admin-stat-icon" style={{ fontSize: "1.5rem" }}>&#x1F4E6;</div>
                  <span className="admin-stat-value">{stats.codCompleted}</span>
                  <span className="admin-stat-label">عند الاستلام</span>
                </div>
              </div>
            )}

            {/* Qi Revenue + Today Summary */}
            {stats && (
              <div style={{ display: "flex", gap: "16px", marginBottom: "24px", flexWrap: "wrap" }}>
                <div className="ap-summary-card">
                  <span className="ap-summary-label">إيرادات Qi Card</span>
                  <span className="ap-summary-value">{formatPrice(stats.qiRevenue)}</span>
                </div>
                <div className="ap-summary-card">
                  <span className="ap-summary-label">طلبات اليوم</span>
                  <span className="ap-summary-value">{stats.todayCount} طلب</span>
                </div>
                <div className="ap-summary-card">
                  <span className="ap-summary-label">إيرادات اليوم</span>
                  <span className="ap-summary-value">{formatPrice(stats.todayRevenue)}</span>
                </div>
              </div>
            )}

            {/* Filters */}
            <div className="ap-filters">
              <input
                type="text"
                className="ap-search"
                placeholder="بحث بالاسم، الهاتف، رقم الفاتورة، رقم الطلب..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              <div className="ap-filter-row">
                <div className="ap-filter-group">
                  <span className="ap-filter-label">الحالة:</span>
                  {["all", "completed", "processing", "pending", "failed", "refunded", "cancelled"].map((s) => (
                    <button
                      key={s}
                      className={`ap-filter-btn ${filter === s ? "ap-filter-active" : ""}`}
                      onClick={() => setFilter(s)}
                    >
                      {s === "all" ? "الكل" : getStatusLabel(s)}
                    </button>
                  ))}
                </div>
                <div className="ap-filter-group">
                  <span className="ap-filter-label">الطريقة:</span>
                  {["all", "qi", "cash_on_delivery"].map((m) => (
                    <button
                      key={m}
                      className={`ap-filter-btn ${methodFilter === m ? "ap-filter-active" : ""}`}
                      onClick={() => setMethodFilter(m)}
                    >
                      {m === "all" ? "الكل" : getMethodLabel(m)}
                    </button>
                  ))}
                </div>
              </div>
              <div style={{ color: "var(--white-muted)", fontSize: "0.8rem", marginTop: "4px" }}>
                {filtered.length} نتيجة
              </div>
            </div>

            {/* Payments List */}
            {filtered.length > 0 ? (
              filtered.map((p, i) => (
                <div
                  className="admin-order-card"
                  key={p._id}
                  style={{ animationDelay: `${Math.min(i, 10) * 0.05}s`, cursor: "pointer" }}
                  onClick={() => setExpandedId(expandedId === p._id ? null : p._id)}
                >
                  {/* Payment Header */}
                  <div className="admin-order-header">
                    <div className="admin-order-header-item">
                      <span className="label">الفاتورة</span>
                      <span className="value" style={{ fontSize: "0.8rem" }}>{p.invoiceNumber}</span>
                    </div>
                    <div className="admin-order-header-item">
                      <span className="label">الحالة</span>
                      <span className={`ap-badge ${getStatusClass(p.paymentStatus)}`}>
                        {getStatusLabel(p.paymentStatus)}
                      </span>
                    </div>
                    <div className="admin-order-header-item">
                      <span className="label">الطريقة</span>
                      <span className="value">
                        {p.paymentMethod === "qi" ? (
                          <span style={{ color: "#00b4d8", fontWeight: 700 }}>Qi Card</span>
                        ) : (
                          getMethodLabel(p.paymentMethod)
                        )}
                      </span>
                    </div>
                    <div className="admin-order-header-item">
                      <span className="label">المبلغ</span>
                      <span className="value" style={{ color: "var(--gold)" }}>{formatPrice(p.totalAmount)}</span>
                    </div>
                    <div className="admin-order-header-item">
                      <span className="label">العميل</span>
                      <span className="value">{p.customer?.name}</span>
                    </div>
                    <div className="admin-order-header-item">
                      <span className="label">التاريخ</span>
                      <span className="value">{moment(p.createdAt).fromNow()}</span>
                    </div>
                  </div>

                  {/* Expanded Details */}
                  {expandedId === p._id && (
                    <div className="ap-details">
                      {/* Customer Info */}
                      <div className="ap-detail-section">
                        <h6 className="ap-detail-title">معلومات العميل</h6>
                        <div className="ap-detail-grid">
                          <div className="ap-detail-item">
                            <span className="ap-detail-label">الاسم</span>
                            <span className="ap-detail-value">{p.customer?.name}</span>
                          </div>
                          <div className="ap-detail-item">
                            <span className="ap-detail-label">الهاتف</span>
                            <span className="ap-detail-value">
                              <a href={`tel:${p.customer?.phone}`} style={{ color: "var(--gold-light)" }}>{p.customer?.phone}</a>
                            </span>
                          </div>
                          <div className="ap-detail-item">
                            <span className="ap-detail-label">المحافظة</span>
                            <span className="ap-detail-value">{p.customer?.city || "-"}</span>
                          </div>
                          <div className="ap-detail-item">
                            <span className="ap-detail-label">العنوان</span>
                            <span className="ap-detail-value">{p.customer?.address || "-"}</span>
                          </div>
                        </div>
                      </div>

                      {/* Payment Info */}
                      <div className="ap-detail-section">
                        <h6 className="ap-detail-title">تفاصيل الدفع</h6>
                        <div className="ap-detail-grid">
                          <div className="ap-detail-item">
                            <span className="ap-detail-label">رقم الفاتورة</span>
                            <span className="ap-detail-value">{p.invoiceNumber}</span>
                          </div>
                          <div className="ap-detail-item">
                            <span className="ap-detail-label">رقم الطلب</span>
                            <span className="ap-detail-value">{p.qi?.merchantReference || "-"}</span>
                          </div>
                          <div className="ap-detail-item">
                            <span className="ap-detail-label">المبلغ الفرعي</span>
                            <span className="ap-detail-value">{formatPrice(p.subtotal)}</span>
                          </div>
                          <div className="ap-detail-item">
                            <span className="ap-detail-label">رسوم التوصيل</span>
                            <span className="ap-detail-value">{formatPrice(p.shippingFee)}</span>
                          </div>
                          <div className="ap-detail-item">
                            <span className="ap-detail-label">المبلغ الإجمالي</span>
                            <span className="ap-detail-value" style={{ color: "var(--gold)", fontWeight: 700 }}>{formatPrice(p.totalAmount)}</span>
                          </div>
                          <div className="ap-detail-item">
                            <span className="ap-detail-label">تاريخ الإنشاء</span>
                            <span className="ap-detail-value">{moment(p.createdAt).format("YYYY/MM/DD - HH:mm")}</span>
                          </div>
                          {p.paidAt && (
                            <div className="ap-detail-item">
                              <span className="ap-detail-label">تاريخ الدفع</span>
                              <span className="ap-detail-value">{moment(p.paidAt).format("YYYY/MM/DD - HH:mm")}</span>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Qi Details - only for Qi payments */}
                      {p.paymentMethod === "qi" && (
                        <div className="ap-detail-section ap-qi-section">
                          <h6 className="ap-detail-title" style={{ color: "#00b4d8" }}>
                            Qi Card - تفاصيل البوابة
                          </h6>
                          <div className="ap-detail-grid">
                            <div className="ap-detail-item">
                              <span className="ap-detail-label">Qi Payment ID</span>
                              <span className="ap-detail-value ap-mono">{p.qi?.paymentId || "-"}</span>
                            </div>
                            <div className="ap-detail-item">
                              <span className="ap-detail-label">Qi Request ID</span>
                              <span className="ap-detail-value ap-mono">{p.qi?.requestId || "-"}</span>
                            </div>
                            <div className="ap-detail-item">
                              <span className="ap-detail-label">حالة Qi</span>
                              <span className="ap-detail-value">
                                <span className={`ap-badge ${p.qi?.status === "SUCCESS" ? "ap-badge-completed" : p.qi?.status === "FAILED" || p.qi?.status === "AUTHENTICATION_FAILED" ? "ap-badge-failed" : "ap-badge-processing"}`}>
                                  {getQiStatusLabel(p.qi?.status)}
                                </span>
                              </span>
                            </div>
                            <div className="ap-detail-item">
                              <span className="ap-detail-label">Transaction ID</span>
                              <span className="ap-detail-value ap-mono">{p.qi?.transactionId || "-"}</span>
                            </div>
                            {p.qi?.details?.maskedPan && (
                              <div className="ap-detail-item">
                                <span className="ap-detail-label">رقم البطاقة</span>
                                <span className="ap-detail-value ap-mono">{p.qi.details.maskedPan}</span>
                              </div>
                            )}
                            {p.qi?.details?.paymentSystem && (
                              <div className="ap-detail-item">
                                <span className="ap-detail-label">نوع البطاقة</span>
                                <span className="ap-detail-value">{p.qi.details.paymentSystem}</span>
                              </div>
                            )}
                            {p.qi?.details?.rrn && (
                              <div className="ap-detail-item">
                                <span className="ap-detail-label">RRN</span>
                                <span className="ap-detail-value ap-mono">{p.qi.details.rrn}</span>
                              </div>
                            )}
                            {p.qi?.details?.authId && (
                              <div className="ap-detail-item">
                                <span className="ap-detail-label">Auth ID</span>
                                <span className="ap-detail-value ap-mono">{p.qi.details.authId}</span>
                              </div>
                            )}
                            {p.qi?.details?.resultCode && (
                              <div className="ap-detail-item">
                                <span className="ap-detail-label">Result Code</span>
                                <span className="ap-detail-value ap-mono">{p.qi.details.resultCode}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Products */}
                      <div className="ap-detail-section">
                        <h6 className="ap-detail-title">المنتجات</h6>
                        <div className="admin-order-products" style={{ padding: 0 }}>
                          {p.items?.map((item, idx) => (
                            <div className="admin-order-product-row" key={idx}>
                              {item.productId && (
                                <img
                                  src={`/api/v1/products/product-photo/${item.productId}`}
                                  className="admin-order-product-img"
                                  alt={item.name}
                                  loading="lazy"
                                />
                              )}
                              <div className="admin-order-product-info">
                                <h6>{item.name}</h6>
                                <p>x{item.quantity || 1}</p>
                              </div>
                              <span className="admin-order-product-price">
                                {formatPrice(item.price * (item.quantity || 1))}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Notes */}
                      {p.notes && (
                        <div style={{ padding: "12px 16px", color: "var(--white-muted)", fontSize: "0.85rem", borderTop: "1px solid var(--glass-border)" }}>
                          <strong style={{ color: "var(--gold)" }}>ملاحظات:</strong> {p.notes}
                        </div>
                      )}

                      {/* Quick Actions */}
                      <div style={{ padding: "12px 16px", display: "flex", gap: "10px", flexWrap: "wrap", borderTop: "1px solid var(--glass-border)" }}>
                        <a
                          href={`/invoice/${p.accessToken}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="ap-action-btn"
                          onClick={(e) => e.stopPropagation()}
                        >
                          عرض الفاتورة
                        </a>
                        {p.customer?.phone && (
                          <a
                            href={`tel:${p.customer.phone}`}
                            className="ap-action-btn"
                            onClick={(e) => e.stopPropagation()}
                          >
                            اتصال بالعميل
                          </a>
                        )}
                        {/* Refund Button — only for completed Qi payments */}
                        {p.paymentStatus === "completed" && p.paymentMethod === "qi" && p.qi?.paymentId && (
                          <button
                            className="ap-action-btn"
                            style={{ background: "rgba(255, 165, 0, 0.15)", color: "#ffa500", border: "1px solid rgba(255, 165, 0, 0.3)" }}
                            disabled={actionLoading === p._id}
                            onClick={(e) => {
                              e.stopPropagation();
                              setConfirmAction({ type: "refund", payment: p });
                            }}
                          >
                            {actionLoading === p._id ? "جاري..." : "استرجاع المبلغ"}
                          </button>
                        )}
                        {/* Cancel Button — only for pending/processing payments */}
                        {["pending", "processing"].includes(p.paymentStatus) && (
                          <button
                            className="ap-action-btn"
                            style={{ background: "rgba(255, 59, 48, 0.15)", color: "#ff3b30", border: "1px solid rgba(255, 59, 48, 0.3)" }}
                            disabled={actionLoading === p._id}
                            onClick={(e) => {
                              e.stopPropagation();
                              setConfirmAction({ type: "cancel", payment: p });
                            }}
                          >
                            {actionLoading === p._id ? "جاري..." : "إلغاء العملية"}
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))
            ) : (
              <div className="empty-state" style={{ marginTop: "40px" }}>
                <div className="empty-state-icon">&#x1F4B3;</div>
                <h4>لا توجد معاملات</h4>
                <p>{search || filter !== "all" || methodFilter !== "all" ? "لا توجد نتائج مطابقة للفلتر" : "ستظهر المعاملات الجديدة هنا"}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      {confirmAction && (
        <div
          style={{
            position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
            background: "rgba(0,0,0,0.7)", zIndex: 9999,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}
          onClick={() => setConfirmAction(null)}
        >
          <div
            style={{
              background: "var(--bg-card, #1a1a2e)", borderRadius: "16px",
              padding: "28px", maxWidth: "420px", width: "90%",
              border: "1px solid var(--glass-border, #333)",
              textAlign: "center",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ fontSize: "2.5rem", marginBottom: "12px" }}>
              {confirmAction.type === "refund" ? "\u26A0\uFE0F" : "\u274C"}
            </div>
            <h4 style={{ color: "#fff", marginBottom: "8px" }}>
              {confirmAction.type === "refund" ? "تأكيد الاسترجاع" : "تأكيد الإلغاء"}
            </h4>
            <p style={{ color: "var(--white-muted, #aaa)", fontSize: "0.9rem", marginBottom: "6px" }}>
              {confirmAction.type === "refund"
                ? `هل أنت متأكد من استرجاع مبلغ ${formatPrice(confirmAction.payment.totalAmount)} للعميل ${confirmAction.payment.customer?.name}؟`
                : `هل أنت متأكد من إلغاء عملية الدفع للعميل ${confirmAction.payment.customer?.name}؟`
              }
            </p>
            <p style={{ color: "#ff3b30", fontSize: "0.8rem", marginBottom: "20px" }}>
              {confirmAction.type === "refund"
                ? "سيتم إرجاع المبلغ إلى بطاقة العميل عبر Qi Card"
                : "لا يمكن التراجع عن هذا الإجراء"
              }
            </p>
            <div style={{ display: "flex", gap: "10px", justifyContent: "center" }}>
              <button
                style={{
                  padding: "10px 24px", borderRadius: "8px", border: "1px solid var(--glass-border, #333)",
                  background: "transparent", color: "#fff", cursor: "pointer", fontSize: "0.9rem",
                }}
                onClick={() => setConfirmAction(null)}
                disabled={actionLoading}
              >
                إلغاء
              </button>
              <button
                style={{
                  padding: "10px 24px", borderRadius: "8px", border: "none", cursor: "pointer", fontSize: "0.9rem",
                  fontWeight: 700,
                  background: confirmAction.type === "refund" ? "rgba(255,165,0,0.9)" : "rgba(255,59,48,0.9)",
                  color: "#fff",
                }}
                onClick={() => {
                  if (confirmAction.type === "refund") {
                    handleRefund(confirmAction.payment);
                  } else {
                    handleCancel(confirmAction.payment);
                  }
                }}
                disabled={actionLoading}
              >
                {actionLoading
                  ? "جاري التنفيذ..."
                  : confirmAction.type === "refund" ? "تأكيد الاسترجاع" : "تأكيد الإلغاء"
                }
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default AdminPayments;
